import {
  FilterableSurveyCategory,
  SurveyCategory,
} from '@/client/security-culture/types';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import {
  ChartEmptyState,
  ChartEmptyStateType,
} from '@/components/dashboards/admin';
import { FlexContainer } from '@/ui/styled-ui';
import { SCDemoCategoriesData } from '@/utils/helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CardInfo } from './CardInfo';
import { SCLegend } from './SCLegend';
import { SecurityCultureDashboardBarChart } from './SecurityCultureDashboardBarChart';
import { SecurityCultureDashboardBarChartLoading } from './SecurityCultureDashboardBarChartLoading';

const StyledCardContainer = styled(FlexContainer)<{
  isMainDashboard?: boolean;
  isV2?: boolean;
}>`
  justify-content: space-between;
  grid-area: sb;
  background-color: ${({ isV2 }) =>
    isV2 ? 'var(--white-main)' : 'var(--beige-main)'};
  padding: var(--default-padding) var(--big-padding);
  border-radius: var(--medium-border-radius);
  margin: 0;
  overflow: hidden;

  &.stretch-children {
    .children {
      overflow-y: hidden;
      overflow-x: auto;
      padding-bottom: ${({ isMainDashboard }) =>
        isMainDashboard ? 0 : 'var(--medium-padding)'};

      > div {
        flex: 1;

        > div {
          width: 100%;
        }
      }

      &::-webkit-scrollbar {
        ${({ isMainDashboard }) => (isMainDashboard ? 'display: none' : '')};
      }
      &::-webkit-scrollbar-track {
        background-color: transparent;
      }
      &::-webkit-scrollbar-thumb {
        border-color: var(--beige-main);
      }
    }
  }
`;

type SCScoreBarsProps = {
  categories?: SurveyCategory[];
  filterableCategories?: FilterableSurveyCategory[];
  surveyId: string | undefined;
  isLoading?: boolean;
  isMainDashboard?: boolean;
  isEmpty?: boolean;
  isV2?: boolean;
};

const getCategoryMaxDisplayValue = (
  category: FilterableSurveyCategory | SurveyCategory,
) => {
  const score = 'score' in category ? category.score : 0;
  const benchmarkScore =
    'benchmarkScore' in category ? category.benchmarkScore : 0;
  const result = 'result' in category ? category.result : 0;
  const benchmarkResult =
    'benchmarkResult' in category ? category.benchmarkResult : 0;

  return Math.max(score, benchmarkScore, result, benchmarkResult);
};

export const SCScoreBars: React.FC<SCScoreBarsProps> = ({
  categories,
  filterableCategories,
  surveyId,
  isLoading = false,
  isMainDashboard,
  isEmpty,
  isV2 = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const hasNoCategories = !(categories?.length || filterableCategories?.length);
  const showEmptyState = !isLoading && (isEmpty || hasNoCategories);

  return (
    <StyledCardContainer
      id="sc-score-bars-card"
      isMainDashboard={isMainDashboard}
      direction="column"
      gap={24}
      className={showEmptyState ? '' : 'stretch-children'}
      isV2={isV2}
    >
      <CardInfo
        id="security-culture-score-bars"
        title={
          isMainDashboard
            ? t('security.culture')
            : t('security.culture.score.bars')
        }
        redTitle={isEmpty ? t('security.culture.notMeasured') : undefined}
        subtitle={
          isMainDashboard
            ? t('security.culture.category.score.subtitle')
            : undefined
        }
        tooltip={
          isMainDashboard
            ? t('security.culture.score.bars.tooltip.main')
            : t('security.culture.score.bars.tooltip')
        }
        style={{ fontSize: '14px', fontWeight: 500 }}
        className={
          isMainDashboard && !isEmpty
            ? isV2
              ? 'card-info-link v2'
              : 'card-info-link'
            : ''
        }
        onClick={() =>
          navigate(
            RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY](surveyId),
          )
        }
      />
      {showEmptyState ? (
        <ChartEmptyState
          type={ChartEmptyStateType.SC}
          noBorder
          noMargin
          transparent
          minHeight={310}
          title={t('dashboard.securityCulture.scoresByCategory.empty.title')}
          description={t(
            'dashboard.securityCulture.scoresByCategory.empty.description',
          )}
        />
      ) : (
        <FlexContainer
          direction="row"
          justify="flex-start"
          className="children"
          align="stretch"
        >
          <>
            {isLoading ? (
              Array.from(Array(6)).map((_, index) => (
                <SecurityCultureDashboardBarChartLoading
                  key={`loading-${index}`}
                  isMainDashboard
                />
              ))
            ) : isEmpty && isMainDashboard ? ( // Demo dashboard
              SCDemoCategoriesData.map((item) => (
                <SecurityCultureDashboardBarChart
                  item={item}
                  key={item.id}
                  maxValue={
                    SCDemoCategoriesData.reduce(
                      (max, item) => (item.result > max ? item.result : max),
                      0,
                    ) + 2
                  }
                  isMainDashboard={isMainDashboard}
                  isEmpty={isEmpty}
                />
              ))
            ) : (categories ?? filterableCategories) && surveyId ? ( // Dashboard with measurements for customers
              (() => {
                const categoryList = (categories ??
                  filterableCategories) as Array<
                  FilterableSurveyCategory | SurveyCategory
                >;

                const maxValue =
                  categoryList.reduce<number>((max, item) => {
                    const value = getCategoryMaxDisplayValue(item);
                    return value > max ? value : max;
                  }, 0) + 2;

                return categoryList
                  .slice()
                  .sort((a: any, b: any) => {
                    if (a.name === 'custom') return 1;
                    if (b.name === 'custom') return -1;
                    return 0;
                  })
                  .map((item: SurveyCategory | FilterableSurveyCategory) => (
                    <SecurityCultureDashboardBarChart
                      item={item}
                      surveyId={surveyId}
                      key={'category' in item ? item.category : item.id}
                      maxValue={maxValue}
                      isLoading={isLoading}
                      isMainDashboard={isMainDashboard}
                    />
                  ));
              })()
            ) : (
              // Empty dashboard for customers
              <p className="mx-auto">{t('security.culture.no.data')}</p>
            )}
          </>
        </FlexContainer>
      )}
      {!showEmptyState && <SCLegend isMainDashboard={isMainDashboard} />}
    </StyledCardContainer>
  );
};
